import type { ThemeAssetEntry } from '@goodtime/engine';

/** Shared helper that wires up a theme's reel-symbol assets from a
 *  single `(slotId → reel-filename-base)` mapping. Resolves the
 *  matching PNG, green-bg companion, and `_win.webm` via
 *  `import.meta.glob` so dropping a new `<theme>/<base>_win.webm`
 *  into the reel folder shows up automatically — no follow-up edit
 *  to the theme file required.
 *
 *  <p>Returns three blocks meant to plug straight into a `Theme`:
 *  <ul>
 *    <li>`slot` — alias+src records keyed by the engine's 9 canonical
 *        slot ids (cherry/lemon/orange/bell/star/seven/gem/scatter/
 *        money) for the static PNG textures.
 *    <li>`slotWin` — alias+src records keyed the same way for the
 *        Veo celebration WebMs. Entries are only present for symbols
 *        whose `_win.webm` exists on disk, so partially-generated
 *        themes don't blow up at module-load.
 *    <li>`winVideoAliasMap` — `slotId → alias` lookup so the symbols
 *        array can attach `winVideoAlias` without restating the
 *        alias-naming convention.
 *  </ul>
 *
 *  <p>Why glob instead of static imports: when the toolgen pipeline
 *  re-runs (e.g. `generateAllWinAnims --theme royal --force`), the
 *  new WebM files appear on disk. With static imports the theme file
 *  has to be edited to import each new URL — easy to forget, easy to
 *  drift out of sync. With glob, Vite re-resolves the matched set on
 *  HMR and the theme picks up the new files free.
 */

// Stage 3 install-excision via source restructure (2026-06-06,
// option 3 in the future-proofing analysis). The glob is narrowed
// to FOREST ONLY — every other biome's reel symbols + win-anims
// are CDN-only. Forest stays bundled as the cold-launch fallback
// theme so a fresh install with no network can still play
// Forest Reels offline. Non-forest themes get downloaded via
// {@link BiomePackManager} on first unlock; until then they
// resolve through {@link AssetManifestClient} → CloudFront.
//
// This avoids Vite-internals coupling (no plugin) and survives
// any Vite version upgrade — only depends on our own
// AssetManifestClient API. Dev DX trade-off: HMR-on-asset-drop
// only works for forest; non-forest art iteration goes through
// the toolgen → sync → manifest → CDN loop (same path as
// production, ~30s round-trip).
const PNGS = import.meta.glob('../assets/reel/forest/*.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const WEBMS = import.meta.glob('../assets/reel/forest/*_win.webm', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

// The HEVC-with-alpha copies (`_win.mov`) WebKit plays in place of the
// WebMs, whose alpha it drops. Only forest's are in the repo (bundled, as
// its WebMs are); the other themes' are kept outside it, published to the
// CDN beside their WebMs, and reached through the manifest by their path.
const MOVS = import.meta.glob('../assets/reel/forest/*_win.mov', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const findUrl = (records: Record<string, string>, suffix: string): string | undefined => {
  for (const [path, url] of Object.entries(records)) {
    if (path.endsWith(suffix)) return url;
  }
  return undefined;
};

// Stage 3 (2026-06-06) — findPath retired. The manifest path is now
// deterministically derived from (themeId, base) inside buildSlotAssets,
// so we don't need to walk the glob records to look it up. This
// matters in CDN-only builds where the glob is narrowed to forest
// only — non-forest paths must still be emitted on entries even when
// no bundled URL exists for them.

export interface BuiltSlotAssets {
  /** `slot` block for `theme.assets.slot` — alias + PNG src per slot id. */
  slot: Record<string, ThemeAssetEntry>;
  /** `slotWin` block for `theme.assets.slotWin` — alias + WebM src per
   *  slot id whose `_win.webm` exists on disk. */
  slotWin: Record<string, ThemeAssetEntry>;
  /** `slotId → winVideoAlias` lookup for attaching to the symbols
   *  array. Always populated for every entry in the mapping, even
   *  when the WebM isn't on disk yet — the engine tolerates a missing
   *  alias (it just won't play a clip on win). */
  winVideoAliasMap: Record<string, string>;
}

/** Build a theme's reel-asset blocks from the engine-slot-id → asset-
 *  filename-base mapping. The filename-base is the canonical reel/
 *  PNG name without extension (e.g. `royal_banner` for `royal_banner.png`
 *  and `royal_banner_win.webm`).
 *
 *  @param themeId    The theme's reel-folder name, e.g. `'royal'`.
 *  @param mapping    Engine slot id (`cherry`/`lemon`/…/`money`) →
 *                    reel-filename-base.
 *  @param fallback   Optional fallback URL for symbols whose PNG is
 *                    missing — useful during pipeline runs that
 *                    haven't yet produced every asset. Defaults to
 *                    leaving the slot entry absent.
 */
export function buildSlotAssets(
  themeId: string,
  mapping: Record<string, string>,
  fallback?: string,
): BuiltSlotAssets {
  const slot: Record<string, ThemeAssetEntry> = {};
  const slotWin: Record<string, ThemeAssetEntry> = {};
  const winVideoAliasMap: Record<string, string> = {};
  for (const [slotId, base] of Object.entries(mapping)) {
    const pngUrl = findUrl(PNGS, `/${themeId}/${base}.png`) ?? fallback;
    // Stage 3 — derive the manifest path alongside the bundled URL
    // so the AssetManifestClient can swap in the CDN URL once the
    // manifest loads. The path is always deterministically derivable
    // from themeId + base; it doesn't depend on whether the file
    // exists in the glob (the glob is narrowed in CDN-only builds,
    // but the file is still on disk + in the manifest).
    const pngPath = `reel/${themeId}/${base}.png`;
    const webmUrl = findUrl(WEBMS, `/${themeId}/${base}_win.webm`);
    const webmPath = `reel/${themeId}/${base}_win.webm`;
    // Stage 3 install-excision (2026-06-06) — emit entries even when
    // pngUrl is undefined, so non-forest themes in a CDN-only build
    // still appear in the slot record (with only {@link path} set).
    // The AssetManifestClient resolves them via CDN at load time.
    slot[slotId] = {
      alias: `${themeId}:${slotId}`,
      ...(pngUrl ? { src: pngUrl } : {}),
      path: pngPath,
    };
    const winAlias = `${themeId}:${slotId}:win:video`;
    winVideoAliasMap[slotId] = winAlias;
    slotWin[slotId] = {
      alias: winAlias,
      ...(webmUrl ? { src: webmUrl } : {}),
      path: webmPath,
      hevc: hevcCopy(webmPath),
    };
  }
  return { slot, slotWin, winVideoAliasMap };
}

/** The HEVC-with-alpha copy beside a reel `_win.webm` (`reel/<theme>/<x>_win.mov`):
 *  its manifest path, and its bundled URL for forest. */
export function hevcCopy(webmPath: string): { src?: string; path: string } {
  const path = webmPath.replace(/\.webm$/, '.mov');
  const src = findUrl(MOVS, `/${path.replace(/^reel\//, '')}`);
  return src ? { src, path } : { path };
}

/** A hand-written theme's `slotWin` block with each WebM's HEVC copy. */
export function withHevcCopies(slotWin: Record<string, ThemeAssetEntry>): Record<string, ThemeAssetEntry> {
  const out: Record<string, ThemeAssetEntry> = {};
  for (const [id, entry] of Object.entries(slotWin)) {
    out[id] = entry.path?.endsWith('.webm') ? { ...entry, hevc: hevcCopy(entry.path) } : entry;
  }
  return out;
}
